import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { listOfValuesApiService } from 'src/services';
import { DEFAULT } from 'src/constants';

export const useFieldValuesSettingsStore = defineStore('field_values_setting', {
  state: () => ({
    list_of_values: {} as dynamicObj,
    list_options_mapping: {} as dynamicObj,
    lov_list: [] as any,
    processing: false,
    table_columns: [
      { label: 'Value', name: 'Value' },
      { label: 'sub_organizations', name: 'sub_organizations' },
      { label: 'Synonyms', name: 'Synonyms' },
      { label: 'language', name: 'language' },
      { label: 'label', name: 'label' },
      {
        label: 'actions',
        name: 'actions',
        class: 'text-center',
        style: { width: '5%' },
      },
    ],
  }),
  getters: {
    getListOfValues: (state: any) => (list_name: string) => {
      if (!state.list_options_mapping.hasOwnProperty(list_name)) {
        return [];
      }

      return state.list_options_mapping[list_name] as dynamicObj[];
    },
  },
  actions: {
    getListOfValuesAsSelectList(list_name: string): any {
      if (!this.list_of_values.hasOwnProperty(list_name)) {
        return [];
      }
      const list = this.list_of_values[list_name].lov_items as dynamicObj[];
      const option_list = [];
      for (const [index, value] of Object.entries(list)) {
        const item = JSON.parse(JSON.stringify(value));
        // API now returns value as array of translations, label as string name
        item['label'] = value.value;
        // value.value is now the translations array — use first translated_value if available
        const translations = Array.isArray(value.label) ? value.label : [];
        if (translations.length > 0 && translations[0].translated_value) {
          item['label'] = translations[0].translated_value;
        }
        option_list.push(item);
      }

      return option_list as any;
    },

    addNameToLovList(list_name: string) {
      if (!list_name || this.list_of_values.hasOwnProperty(list_name)) {
        return;
      }
      list_name = String(list_name.trim());
      this.list_of_values[list_name] = {} as dynamicObj;
      this.list_of_values[list_name]['lov_items'] = {} as dynamicObj;
    },

    async populateListsAndValues() {
      try {
        this.processing = true;
        this.list_options_mapping = {};

        const list_names = Object.keys(this.list_of_values);
        const list_names_str = list_names.join(',');
        if (!list_names_str) {
          return;
        }
        // CACHE
        await this.getMultipleLists(list_names_str);

        for (const [list_name, option_list] of Object.entries(
          this.list_of_values,
        )) {
          const list_keys = Object.keys(option_list['lov_items']);
          this.list_options_mapping[list_name] = list_keys;
        }
      } catch (err: any) {
        logError(err);
      } finally {
        this.processing = false;
      }
    },

    async getLists() {
      this.list_of_values = {} as dynamicObj;
      const response = await listOfValuesApiService.getLists();
      if (response.success === false) {
        throw { message: response.message };
      }
      if (!response.data) {
        throw response;
      }

      for (const [index, listInfo] of Object.entries(response.data)) {
        const item = listInfo as dynamicObj;

        let name: string;
        if (typeof item.value === 'string') {
          name = item.value;
        } else if (Array.isArray(item.value) && item.value.length > 0) {
          name = item.value[0].translated_value || item.value[0];
        } else if (item.name) {
          name = String(item.name);
        } else if (item.label) {
          name = String(item.label);
        } else {
          name = String(item.value);
        }

        this.list_of_values[name] = item;
      }
    },

    async getLovList() {
      try {
        this.processing = true;
        this.lov_list = [];
        this.lov_list.push('');
        await this.getLists();
        for (const [list_name, index] of Object.entries(this.list_of_values)) {
          this.lov_list.push(list_name);
        }
      } catch (err: any) {
        logError(err);
      } finally {
        this.processing = false;
      }
    },

    async getValuesForList(list_name: string, use_sub_org = true) {
      this.list_of_values[list_name]['lov_items'] = {} as dynamicObj;
      const response = await listOfValuesApiService.getValuesForList(
        list_name,
        use_sub_org,
      );

      if (response.success === false) {
        throw { message: response.message };
      }

      if (!response.data) {
        throw response;
      }

      for (const [index, valueInfo] of Object.entries(response.data)) {
        const item = valueInfo as dynamicObj;
        /* Using id if available, otherwise label with index as fallback */
        const name = item.id ? String(item.id) : String(item.label) + '\u200B' + index;

        //With change of LoV logic, the save has changed as well.
        //This code chunk will make sure the default is always shown on top
        const translations = valueInfo as { org_id: string; value: any[] };
        if (translations['org_id'] === DEFAULT && Array.isArray(translations['value'])) {
          const defaultLov = translations['value'].findIndex(
            (item: any) => item.org_id === DEFAULT,
          );

          if (defaultLov !== -1) {
            const [defaultItem] = translations['value'].splice(defaultLov, 1);
            translations['value'].unshift(defaultItem);
          }
        }
        this.list_of_values[list_name]['lov_items'][name] = valueInfo;
      }
    },

    async getMultipleLists(list_names: string) {
      const response =
        await listOfValuesApiService.getMultipleListOfValues(list_names);

      if (response.success === false) {
        throw { message: response.message };
      }

      if (!response.data) {
        throw response;
      }

      this.addResultToListOfValues(response.data);
    },

    addResultToListOfValues(data: dynamicObj) {
      for (const [list_name, list_items] of Object.entries(data)) {
        for (const [name, valueInfo] of Object.entries(list_items)) {
          const item = valueInfo as dynamicObj;

          /* Adding zero width space to avoid key collision for duplicate values in different orgs
          This is only used when preparing list of values as for display, index is not being used at all */
          const name = String(item.value) + '\u200B'; 
          this.list_of_values[list_name]['lov_items'][name] = item;
        }
      }
    },
  },
});
